import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  createPlatformLicense,
  fetchCommercialPlansCatalog,
  fetchOrganizations,
  fetchPlatformLicenses,
} from "@/lib/platformApi";
import {
  billingCadenceLabel,
  commercialPlanLabel,
  commercialPlanModuleIds,
  commercialPlanOptions,
} from "@/lib/commercialPlanUi";

function licenseSummary(license = {}) {
  if (license.commercial_plan) {
    const cadence = license.billing_cadence ? ` · ${billingCadenceLabel(license.billing_cadence)}` : "";
    const limits = [
      license.authorized_users != null ? `${license.authorized_users} usuarios` : "",
      license.authorized_clients != null ? `${license.authorized_clients} clientes` : "",
    ].filter(Boolean).join(" · ");
    return `Plan ${commercialPlanLabel(license.commercial_plan)}${cadence}${limits ? ` · ${limits}` : ""}`;
  }
  const active = Object.entries(license.modules || {}).filter(([, enabled]) => enabled).map(([id]) => id);
  return active.length ? `Licencia legacy · ${active.join(", ")}` : "Licencia legacy";
}

function parseDuplicateEmitError(err) {
  const detail = err?.response?.data?.detail;
  if (typeof detail === "object" && detail?.code === "LICENSE_EMIT_DUPLICATE") {
    return detail.message || "Ya existe una licencia activa idéntica.";
  }
  if (typeof detail === "string" && /LICENSE_EMIT_DUPLICATE|misma vigencia/i.test(detail)) {
    return detail;
  }
  return null;
}

export default function PlatformLicenses() {
  const [orgs, setOrgs] = useState([]);
  const [licenses, setLicenses] = useState([]);
  const [planOptions, setPlanOptions] = useState([]);
  const [organizationId, setOrganizationId] = useState("");
  const [validFrom, setValidFrom] = useState("2026-01-01");
  const [validTo, setValidTo] = useState("2027-12-31");
  const [commercialPlan, setCommercialPlan] = useState("");
  const [billingCadence, setBillingCadence] = useState("annual");
  const [creating, setCreating] = useState(false);

  const reload = useCallback(async () => {
    try {
      const [o, plans] = await Promise.all([
        fetchOrganizations(),
        fetchCommercialPlansCatalog(),
      ]);
      setOrgs(o);
      const resolvedPlans = commercialPlanOptions(plans);
      setPlanOptions(resolvedPlans);
      setCommercialPlan((current) => current || resolvedPlans[0]?.id || "");
      const orgId = organizationId || "";
      setLicenses(await fetchPlatformLicenses(orgId || undefined));
    } catch {
      toast.error("Error cargando licencias");
    }
  }, [organizationId]);

  useEffect(() => {
    reload();
  }, [reload]);

  const selectedOrg = useMemo(
    () => orgs.find((o) => o.id === organizationId) || null,
    [orgs, organizationId],
  );

  const currentLicense = useMemo(
    () => licenses.find((lic) => lic.is_current) || licenses.find((lic) => lic.status === "active") || null,
    [licenses],
  );

  const historyLicenses = useMemo(
    () => licenses.filter((lic) => lic.id !== currentLicense?.id),
    [licenses, currentLicense],
  );

  const selectedPlan = useMemo(
    () => planOptions.find((plan) => plan.id === commercialPlan) || null,
    [planOptions, commercialPlan],
  );

  const emitPayload = useMemo(
    () => ({
      organization_id: organizationId,
      valid_from: validFrom,
      valid_to: validTo,
      commercial_plan: commercialPlan,
      billing_cadence: billingCadence,
      install_on_owner: true,
    }),
    [organizationId, validFrom, validTo, commercialPlan, billingCadence],
  );

  const submitCreate = async (confirmDuplicate = false) => {
    if (!organizationId) {
      toast.error("Seleccione una organización");
      return;
    }
    if (!selectedPlan) {
      toast.error("Seleccione un plan comercial publicado");
      return;
    }
    setCreating(true);
    try {
      const lic = await createPlatformLicense({ ...emitPayload, confirm_duplicate: confirmDuplicate });
      toast.success(`Licencia rev.${lic.revision} creada`);
      reload();
    } catch (err) {
      const dupMsg = parseDuplicateEmitError(err);
      if (dupMsg && !confirmDuplicate) {
        const ok = window.confirm(
          `${dupMsg}\n\n¿Desea emitir una nueva revisión de todas formas?`,
        );
        if (ok) {
          setCreating(false);
          return submitCreate(true);
        }
        return;
      }
      toast.error(err?.response?.data?.detail?.message || err?.response?.data?.detail || "Error al crear licencia");
    } finally {
      setCreating(false);
    }
  };

  const onCreate = async (e) => {
    e.preventDefault();
    await submitCreate(false);
  };

  return (
    <div className="space-y-6">
      <section className="rounded-lg border border-[#0F2D5C]/15 bg-white p-6 shadow-sm">
        <h2 className="text-base font-semibold text-[#0F2D5C]">Licencias emitidas</h2>
        <p className="mt-1 text-sm text-slate-500">
          Control de clientes autorizados: emite por plan o consulta la licencia vigente por organización.
        </p>
        <p className="text-xs text-slate-600 mt-1 leading-relaxed">
          Confirme con el botón de emisión. Abrir esta pantalla o refrescar no crea revisiones.
        </p>
        <form onSubmit={onCreate} className="mt-4 grid gap-3 sm:grid-cols-2" data-testid="platform-license-form">
          <div className="sm:col-span-2">
            <Label>Organización</Label>
            <select
              className="mt-1 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
              value={organizationId}
              onChange={(e) => setOrganizationId(e.target.value)}
            >
              <option value="">Todas las organizaciones</option>
              {orgs.map((o) => (
                <option key={o.id} value={o.id}>
                  {o.name} ({o.rfc})
                </option>
              ))}
            </select>
          </div>
          <div>
            <Label>Vigencia desde</Label>
            <Input type="date" value={validFrom} onChange={(e) => setValidFrom(e.target.value)} />
          </div>
          <div>
            <Label>Vigencia hasta</Label>
            <Input type="date" value={validTo} onChange={(e) => setValidTo(e.target.value)} />
          </div>
          <div>
            <Label>Plan comercial</Label>
            <Select value={commercialPlan} onValueChange={setCommercialPlan}>
              <SelectTrigger className="mt-1" data-testid="platform-license-plan-select">
                <SelectValue placeholder="Seleccione un plan" />
              </SelectTrigger>
              <SelectContent>
                {planOptions.map((plan) => (
                  <SelectItem key={plan.id} value={plan.id}>
                    {plan.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>Cadencia de cobro</Label>
            <Select value={billingCadence} onValueChange={setBillingCadence}>
              <SelectTrigger className="mt-1" data-testid="platform-license-cadence-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="monthly">Mensual</SelectItem>
                <SelectItem value="annual">Anual</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="sm:col-span-2 rounded-md border border-slate-200 bg-slate-50 px-3 py-2 text-xs" data-testid="platform-license-plan-summary">
            {selectedPlan ? (
              <>
                <p className="m-0 font-semibold text-[#0F2D5C]">
                  {selectedPlan.label}: {commercialPlanModuleIds(selectedPlan).join(" · ")}
                </p>
                <p className="m-0 mt-1 text-slate-600">
                  Límite: {selectedPlan.limits?.authorized_users ?? "—"} usuarios · {selectedPlan.limits?.authorized_clients ?? "—"} clientes.
                  Los módulos y límites se toman de la matriz publicada.
                </p>
              </>
            ) : (
              <p className="m-0 text-amber-800">No hay una matriz publicada de planes disponible.</p>
            )}
          </div>
          <div className="sm:col-span-2">
            <Button
              type="submit"
              className="bg-[#0F2D5C] hover:bg-[#0a2247]"
              disabled={creating || !selectedPlan}
              data-testid="platform-create-license-btn"
            >
              {creating ? "Emitiendo…" : "Confirmar emisión"}
            </Button>
          </div>
        </form>
      </section>

      <section className="rounded-lg border border-[#0F2D5C]/15 bg-white p-6 shadow-sm">
        <h2 className="text-base font-semibold text-[#0F2D5C]">Licencia vigente</h2>
        {selectedOrg ? (
          <p className="text-xs text-slate-600 mt-1">
            {selectedOrg.name} ({selectedOrg.rfc})
          </p>
        ) : (
          <p className="text-xs text-slate-600 mt-1">Todas las organizaciones — filas agrupadas por org.</p>
        )}
        {!organizationId && licenses.length ? (
          <ul className="mt-3 divide-y divide-slate-100 text-sm space-y-2" data-testid="platform-license-all-list">
            {licenses.filter((lic) => lic.is_current).map((lic) => (
              <li
                key={lic.id}
                className="rounded-md border border-emerald-200 bg-emerald-50/60 px-3 py-2"
                data-testid="platform-license-current"
              >
                <p className="font-semibold text-[#0F2D5C] m-0">
                  {lic.organization_name || "Organización"} ({lic.organization_rfc || "—"}) · Rev. {lic.revision}
                </p>
                <p className="text-xs text-slate-700 mt-1 m-0">
                  {lic.valid_from} — {lic.valid_to} · {licenseSummary(lic)}
                </p>
              </li>
            ))}
          </ul>
        ) : currentLicense ? (
          <div
            className="mt-3 rounded-md border border-emerald-200 bg-emerald-50/60 px-3 py-2 text-sm"
            data-testid="platform-license-current"
          >
            <p className="font-semibold text-[#0F2D5C] m-0">
              Rev. {currentLicense.revision} · Vigente
            </p>
            <p className="text-xs text-slate-700 mt-1 m-0">
              {currentLicense.valid_from} — {currentLicense.valid_to} · {licenseSummary(currentLicense)}
            </p>
          </div>
        ) : (
          <p className="text-sm text-slate-500 mt-3">Sin licencia activa para esta organización.</p>
        )}
      </section>

      <section className="rounded-lg border border-[#0F2D5C]/15 bg-white p-6 shadow-sm">
        <Accordion type="single" collapsible defaultValue="">
          <AccordionItem value="history" className="border-0">
            <AccordionTrigger className="py-0 hover:no-underline" data-testid="platform-license-history-trigger">
              <div className="text-left">
                <h2 className="text-base font-semibold text-[#0F2D5C] m-0">Historial de revisiones</h2>
                <p className="text-xs text-slate-600 mt-1 font-normal">
                  {historyLicenses.length} revisiones anteriores o sustituidas
                </p>
              </div>
            </AccordionTrigger>
            <AccordionContent>
              <ul className="mt-3 divide-y divide-slate-100 text-sm" data-testid="platform-license-history-list">
                {historyLicenses.length ? (
                  historyLicenses.map((lic) => (
                    <li key={lic.id} className="py-2">
                      <span className="font-medium text-[#0F2D5C]">Rev. {lic.revision}</span>
                      {lic.status === "superseded" ? (
                        <span className="ml-2 text-[10px] uppercase tracking-wide text-slate-500">sustituida</span>
                      ) : lic.status === "active" && !lic.is_current ? (
                        <span className="ml-2 text-[10px] uppercase tracking-wide text-amber-700">reemplazada</span>
                      ) : null}
                      {" · "}
                      {lic.valid_from} — {lic.valid_to}
                      {" · "}
                      {licenseSummary(lic)}
                    </li>
                  ))
                ) : (
                  <li className="py-2 text-slate-500">No hay revisiones anteriores.</li>
                )}
              </ul>
            </AccordionContent>
          </AccordionItem>
        </Accordion>
      </section>
    </div>
  );
}
